/* GENERATED from src/gasket-core.js by build.py — edit the source, not this copy. */
/*
 * R&D Industries — custom gasket geometry + pricing core.
 * Shared, byte-for-byte, by the storefront quoter and the checkout service so the
 * price a customer sees is the price they are charged. No DOM, no network.
 *
 * All internal math is in inches. Prices are in the store currency.
 */
var RDGasketCore = (function () {
  'use strict';

  var MM_PER_IN = 25.4;
  var PI = Math.PI;

  var SHAPES = {
    flange: { label: 'Flange gasket (bolt holes)', fields: ['od', 'id', 'bc', 'holes', 'hole_d'] },
    ring: { label: 'Ring gasket', fields: ['od', 'id'] },
    disc: { label: 'Disc / solid round', fields: ['od'] },
    rect: { label: 'Rectangle / square', fields: ['w', 'l', 'r', 'holes', 'hole_d', 'e'] },
    frame: { label: 'Rectangular frame (window)', fields: ['w', 'l', 'r', 'iw', 'il', 'ir', 'holes', 'hole_d', 'e'] }
  };

  var FIELD_LABELS = {
    od: 'Outer diameter', id: 'Inner diameter', bc: 'Bolt circle', holes: 'Number of holes',
    hole_d: 'Hole diameter', w: 'Width', l: 'Length', r: 'Corner radius',
    iw: 'Opening width', il: 'Opening length', ir: 'Opening corner radius', e: 'Hole inset'
  };
  // Rectangles and frames: holes are optional (0 = none); size and inset only matter when there are holes.
  var RECT_HOLE_FIELDS = { holes: true, hole_d: true, e: true };
  function isRect(shape) { return shape === 'rect' || shape === 'frame'; }

  // Fields that are counts, not lengths (never unit-converted)
  var COUNT_FIELDS = { holes: true };
  // Fields that may be zero
  var OPTIONAL_ZERO = { r: true, ir: true, id: true };

  function round2(x) { return Math.round((x + Number.EPSILON) * 100) / 100; }
  function ceil2(x) { return Math.ceil((x - 1e-9) * 100) / 100; }
  function num(v) {
    if (v === null || v === undefined || v === '') return NaN;
    if (typeof v === 'number') return v;
    var s = String(v).trim();
    // allow fractions like 1-1/2, 1 1/2, 3/4
    var m = s.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
    if (m) return +m[1] + (+m[2] / +m[3]);
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) return +m[1] / +m[2];
    return Number(s);
  }

  function findMaterial(config, materialId) {
    for (var i = 0; i < config.materials.length; i++) {
      if (config.materials[i].id === materialId) return config.materials[i];
    }
    return null;
  }
  function findThickness(material, thicknessId) {
    if (!material) return null;
    for (var i = 0; i < material.thicknesses.length; i++) {
      if (material.thicknesses[i].id === thicknessId) return material.thicknesses[i];
    }
    return null;
  }

  /** Convert raw user dims (strings or numbers, in `units`) to inches. */
  function normalizeDims(shape, rawDims, units) {
    var f = (units === 'mm') ? 1 / MM_PER_IN : 1;
    var out = {};
    var fields = (SHAPES[shape] || { fields: [] }).fields;
    for (var i = 0; i < fields.length; i++) {
      var k = fields[i];
      var v = num(rawDims ? rawDims[k] : undefined);
      if (isNaN(v) && OPTIONAL_ZERO[k]) v = 0;
      if (isNaN(v) && isRect(shape) && k === 'holes') v = 0;
      out[k] = COUNT_FIELDS[k] ? v : v * f;
    }
    if (isRect(shape) && !(out.holes > 0)) { out.holes = 0; if (isNaN(out.hole_d)) out.hole_d = 0; if (isNaN(out.e)) out.e = 0; }
    return out;
  }

  /**
   * Hole centers for a rectangle or frame, in inches from the part's center (x across the
   * width, y along the length). Holes sit on a line `e` in from the outside edge.
   * 2 holes: one at each end of the long direction. 4 or more (even): one in each corner,
   * the rest spaced evenly along the sides, split so spacing is as even as possible.
   */
  function rectHoles(d) {
    var n = Math.round(d.holes) || 0, out = [];
    if (n <= 0) return out;
    var a = d.w - 2 * d.e, b = d.l - 2 * d.e;
    if (n === 2) {
      if (d.w >= d.l) return [{ x: -a / 2, y: 0 }, { x: a / 2, y: 0 }];
      return [{ x: 0, y: -b / 2 }, { x: 0, y: b / 2 }];
    }
    if (n < 4 || n % 2) return out;
    var half = (n - 4) / 2, best = null;
    for (var k = 0; k <= half; k++) {
      var m = half - k;
      var diff = Math.abs(a / (k + 1) - b / (m + 1));
      if (!best || diff < best.diff - 1e-9) best = { k: k, m: m, diff: diff };
    }
    out.push({ x: -a / 2, y: -b / 2 }, { x: a / 2, y: -b / 2 }, { x: a / 2, y: b / 2 }, { x: -a / 2, y: b / 2 });
    for (var i = 1; i <= best.k; i++) {
      var x = -a / 2 + i * a / (best.k + 1);
      out.push({ x: x, y: -b / 2 }, { x: x, y: b / 2 });
    }
    for (var j = 1; j <= best.m; j++) {
      var y = -b / 2 + j * b / (best.m + 1);
      out.push({ x: -a / 2, y: y }, { x: a / 2, y: y });
    }
    return out;
  }

  /** Geometry in inches: bounding box, net area, cut length, hole count. */
  function geometry(shape, d) {
    var g = { bboxW: 0, bboxL: 0, netArea: 0, cutLength: 0, holes: 0 };
    if (shape === 'flange' || shape === 'ring' || shape === 'disc') {
      var od = d.od, id = shape === 'disc' ? 0 : (d.id || 0);
      g.bboxW = g.bboxL = od;
      g.netArea = PI / 4 * (od * od - id * id);
      g.cutLength = PI * od + (id > 0 ? PI * id : 0);
      if (shape === 'flange') {
        var n = Math.round(d.holes) || 0;
        g.holes = n;
        g.netArea -= n * PI / 4 * d.hole_d * d.hole_d;
        g.cutLength += n * PI * d.hole_d;
      }
    } else if (shape === 'rect' || shape === 'frame') {
      var r = Math.min(d.r || 0, Math.min(d.w, d.l) / 2);
      g.bboxW = d.w; g.bboxL = d.l;
      g.netArea = d.w * d.l - (4 - PI) * r * r;
      g.cutLength = 2 * (d.w + d.l) - 8 * r + 2 * PI * r;
      if (shape === 'frame') {
        var ir = Math.min(d.ir || 0, Math.min(d.iw, d.il) / 2);
        g.netArea -= d.iw * d.il - (4 - PI) * ir * ir;
        g.cutLength += 2 * (d.iw + d.il) - 8 * ir + 2 * PI * ir;
      }
      var nh = Math.round(d.holes) || 0;
      if (nh > 0) {
        g.holes = nh;
        g.netArea -= nh * PI / 4 * d.hole_d * d.hole_d;
        g.cutLength += nh * PI * d.hole_d;
      }
    }
    return g;
  }

  /** Returns a list of human-readable problems with the geometry (inches). */
  function validate(shape, d, config, units) {
    var errs = [];
    var lim = config.limits || {};
    var web = lim.min_web_in || 0.125;
    var minHole = lim.min_hole_in || 0.0625;
    var maxW = lim.max_width_in || 36, maxL = lim.max_length_in || 36;
    var u = units === 'mm' ? 'mm' : 'in';
    var show = function (inches) {
      return u === 'mm' ? (Math.round(inches * MM_PER_IN * 10) / 10) + ' mm' : (Math.round(inches * 1000) / 1000) + '"';
    };

    if (!SHAPES[shape]) return ['Pick a gasket shape.'];
    var fields = SHAPES[shape].fields;
    for (var i = 0; i < fields.length; i++) {
      var k = fields[i], v = d[k];
      if (isRect(shape) && RECT_HOLE_FIELDS[k]) continue;
      if (isNaN(v)) { errs.push('Enter the ' + FIELD_LABELS[k].toLowerCase() + '.'); continue; }
      if (v < 0 || (v === 0 && !OPTIONAL_ZERO[k])) errs.push(FIELD_LABELS[k] + ' must be greater than zero.');
    }
    if (errs.length) return errs;

    if (shape === 'flange' || shape === 'ring' || shape === 'disc') {
      if (d.od > Math.min(maxW, maxL)) errs.push('Outer diameter is larger than our ' + show(Math.min(maxW, maxL)) + ' maximum. Call us for oversize or spliced gaskets.');
      if (shape !== 'disc' && d.id > 0 && d.id > d.od - 2 * web) errs.push('Inner diameter must be at least ' + show(2 * web) + ' smaller than the outer diameter.');
    }
    if (shape === 'ring' && !(d.id > 0)) errs.push('Enter the inner diameter (or choose Disc for a solid round).');
    if (shape === 'flange') {
      var n = d.holes;
      if (Math.round(n) !== n || n < 1) errs.push('Number of holes must be a whole number of 1 or more.');
      else if (n > (lim.max_holes || 200)) errs.push('Up to ' + (lim.max_holes || 200) + ' holes can be quoted online.');
      if (d.hole_d < minHole) errs.push('Smallest hole we cut is ' + show(minHole) + '.');
      var outerMargin = d.od / 2 - (d.bc / 2 + d.hole_d / 2);
      var innerMargin = (d.bc / 2 - d.hole_d / 2) - (d.id || 0) / 2;
      if (outerMargin < web) errs.push('Bolt holes are too close to the outer edge (need ' + show(web) + ' of material). Check the bolt circle and hole size.');
      if (innerMargin < web) errs.push('Bolt holes are too close to the inner edge (need ' + show(web) + ' of material).');
      if (n >= 2 && Math.round(n) === n) {
        var gap = d.bc * Math.sin(PI / n) - d.hole_d;
        if (gap < web) errs.push('Holes overlap or are too close together. With ' + n + ' holes on a ' + show(d.bc) + ' bolt circle, holes can be at most ' + show(Math.max(0, d.bc * Math.sin(PI / n) - web)) + ' across.');
      }
    }
    if (shape === 'rect' || shape === 'frame') {
      var small = Math.min(d.w, d.l), big = Math.max(d.w, d.l);
      if (small > Math.min(maxW, maxL) || big > Math.max(maxW, maxL)) errs.push('Largest part we quote online is ' + show(Math.min(maxW, maxL)) + ' × ' + show(Math.max(maxW, maxL)) + '. Call us for larger parts.');
      if ((d.r || 0) > small / 2) errs.push('Corner radius can be at most half the smaller side (' + show(small / 2) + ').');
    }
    if (isRect(shape)) {
      var nh = d.holes;
      if (isNaN(nh) || nh < 0 || Math.round(nh) !== nh) errs.push('Number of holes must be 0 (no holes) or a whole number.');
      else if (nh > 0) {
        if (nh !== 2 && (nh < 4 || nh % 2)) errs.push('Holes can be 0, 2, or an even number of 4 or more. For another pattern, send us your drawing.');
        else if (nh > (lim.max_holes || 200)) errs.push('Up to ' + (lim.max_holes || 200) + ' holes can be quoted online.');
        if (isNaN(d.hole_d) || !(d.hole_d > 0)) errs.push('Enter the hole diameter.');
        else if (d.hole_d < minHole) errs.push('Smallest hole we cut is ' + show(minHole) + '.');
        if (isNaN(d.e) || !(d.e > 0)) errs.push('Enter the hole inset (distance from the outside edge to the hole centers).');
        if (!errs.length) {
          var hr = d.hole_d / 2;
          if (d.e - hr < web) errs.push('Holes are too close to the outside edge. Make the hole inset at least ' + show(hr + web) + '.');
          if (d.w - 2 * d.e <= 0 || d.l - 2 * d.e <= 0) errs.push('The hole inset is too large for this size.');
          var rc = Math.min(d.r || 0, Math.min(d.w, d.l) / 2);
          if (nh >= 4 && rc > d.e) {
            var cornerGap = rc - Math.SQRT2 * (rc - d.e) - hr;
            if (cornerGap < web) errs.push('Corner holes break out of the rounded corners. Use a smaller corner radius or a larger hole inset.');
          }
          if (shape === 'frame') {
            var sideX = (d.w - d.iw) / 2 - d.e - hr, sideY = (d.l - d.il) / 2 - d.e - hr;
            if ((nh >= 4 || d.w >= d.l) && sideX < web || (nh >= 4 || d.w < d.l) && sideY < web)
              errs.push('Holes run into the opening. Leave at least ' + show(web) + ' between the holes and the opening, or use a smaller inset or hole.');
          }
          if (!errs.length) {
            var pts = rectHoles(d), minGap = Infinity;
            for (var p1 = 0; p1 < pts.length; p1++) for (var p2 = p1 + 1; p2 < pts.length; p2++) {
              var dist = Math.sqrt(Math.pow(pts[p1].x - pts[p2].x, 2) + Math.pow(pts[p1].y - pts[p2].y, 2));
              if (dist < minGap) minGap = dist;
            }
            if (minGap - d.hole_d < web) errs.push('Holes overlap or are too close together. Use fewer or smaller holes.');
          }
        }
      }
    }
    if (shape === 'frame') {
      if (d.iw > d.w - 2 * web || d.il > d.l - 2 * web) errs.push('The opening must be at least ' + show(2 * web) + ' smaller than the outside in each direction.');
      if ((d.ir || 0) > Math.min(d.iw, d.il) / 2) errs.push('Opening corner radius can be at most half the opening\'s smaller side.');
    }
    return errs;
  }

  function tierFor(config, qty) {
    var breaks = (config.qty_breaks || [{ min: 1, multiplier: 1 }]).slice().sort(function (a, b) { return a.min - b.min; });
    var cur = breaks[0], next = null;
    for (var i = 0; i < breaks.length; i++) {
      if (qty >= breaks[i].min) cur = breaks[i];
      else { next = breaks[i]; break; }
    }
    return { current: cur, next: next };
  }

  function piecePrice(config, rate, g, psa) {
    var p = config.pricing || {};
    var area = (p.area_basis === 'net') ? g.netArea : g.bboxW * g.bboxL;
    var material = area * rate;
    var cutting = g.cutLength * (p.cut_rate_per_in || 0) + g.holes * (p.per_hole || 0);
    var adhesive = 0;
    if (psa && config.psa && config.psa.enabled) adhesive = area * (config.psa.per_sq_in || 0);
    var base = material + cutting + adhesive;
    return { area: area, material: material, cutting: cutting, adhesive: adhesive, base: base, piece: Math.max(p.min_piece_price || 0, base) };
  }

  /**
   * Full quote. `spec` = { shape, units: 'in'|'mm', dims: {...}, material, thickness, qty, psa }
   * Returns { ok, errors[], unitPrice, lineTotal, ... }
   */
  function quote(spec, config) {
    var res = { ok: false, errors: [], warnings: [] };
    if (!spec || !config) { res.errors.push('Missing quote details.'); return res; }
    var shape = spec.shape;
    var units = spec.units === 'mm' ? 'mm' : 'in';
    var d = normalizeDims(shape, spec.dims || {}, units);
    res.dimsIn = d;

    var mat = findMaterial(config, spec.material);
    var th = findThickness(mat, spec.thickness);
    if (!mat) res.errors.push('Choose a material.');
    else if (!th) res.errors.push('Choose a thickness for ' + mat.name + '.');
    else if (!(th.rate > 0)) res.errors.push(mat.name + ' at ' + th.label + ' needs a quote — call or email us.');

    var qty = num(spec.qty);
    var maxQty = (config.limits && config.limits.max_qty) || 5000;
    if (!(qty >= 1) || Math.round(qty) !== qty) res.errors.push('Quantity must be a whole number of 1 or more.');
    else if (qty > maxQty) res.errors.push('For more than ' + maxQty + ' pieces, call us for production pricing.');

    var gErr = validate(shape, d, config, units);
    res.errors = res.errors.concat(gErr);
    if (gErr.length || !SHAPES[shape]) return res;

    var g = geometry(shape, d);
    res.geometry = g;
    if (res.errors.length) return res;

    // Large parts: no instant price; the customer requests a quote so we can nest them with scrap.
    var lim = config.limits || {};
    var round = shape === 'flange' || shape === 'ring' || shape === 'disc';
    var cap = round ? lim.instant_max_round_in : lim.instant_max_rect_in;
    if (cap > 0 && (round ? d.od : Math.max(d.w, d.l)) > cap + 1e-9) {
      res.quoteOnly = true;
      res.quoteOnlyReason = config.quote_only_note || ('Gaskets over ' + cap + '" are quoted by our team.');
      res.instantLimit = round ? (fmtLen(cap, units) + ' diameter') : (fmtLen(cap, units) + ' × ' + fmtLen(cap, units));
      res.shape = shape; res.units = units; res.qty = qty;
      res.material = { id: mat.id, name: mat.name };
      res.thickness = { id: th.id, label: th.label, in: th.in };
      res.summary = summarize(res, spec);
      return res;
    }

    var psa = !!spec.psa && !!(config.psa && config.psa.enabled);
    var pp = piecePrice(config, th.rate, g, psa);
    var tier = tierFor(config, qty);
    var p = config.pricing || {};
    var setupEach = (p.setup_fee || 0) / qty;
    var unit = ceil2(pp.piece * tier.current.multiplier + setupEach);
    var line = round2(unit * qty);
    if ((p.min_order_total || 0) > 0 && line < p.min_order_total) {
      unit = ceil2(p.min_order_total / qty);
      line = round2(unit * qty);
      res.warnings.push('Includes the ' + money(p.min_order_total, config) + ' minimum order.');
    }

    res.ok = true;
    res.shape = shape;
    res.units = units;
    res.qty = qty;
    res.psa = psa;
    res.material = { id: mat.id, name: mat.name };
    res.thickness = { id: th.id, label: th.label, in: th.in };
    res.rate = th.rate;
    res.breakdown = pp;
    res.multiplier = tier.current.multiplier;
    res.unitPrice = unit;
    res.lineTotal = line;
    if (tier.next) {
      var nextUnit = ceil2(pp.piece * tier.next.multiplier + (p.setup_fee || 0) / tier.next.min);
      if (nextUnit < unit) res.nextBreak = { min: tier.next.min, unitPrice: nextUnit, savePct: Math.round((1 - tier.next.multiplier / tier.current.multiplier) * 100) };
    }
    res.summary = summarize(res, spec);
    return res;
  }

  function fmtLen(inches, units) {
    if (units === 'mm') return (Math.round(inches * MM_PER_IN * 100) / 100) + ' mm';
    return (Math.round(inches * 10000) / 10000) + '"';
  }

  /** Ordered [label, value] pairs for cart line properties and emails. */
  function summarize(res, spec) {
    var d = res.dimsIn, u = res.units, out = [];
    out.push(['Shape', SHAPES[res.shape].label]);
    out.push(['Material', res.material.name + ' — ' + res.thickness.label + ' thick']);
    if (res.shape === 'flange' || res.shape === 'ring' || res.shape === 'disc') {
      out.push(['Outer diameter', fmtLen(d.od, u)]);
      if (res.shape !== 'disc' && d.id > 0) out.push(['Inner diameter', fmtLen(d.id, u)]);
      if (res.shape === 'flange') {
        out.push(['Bolt circle', fmtLen(d.bc, u)]);
        out.push(['Bolt holes', d.holes + ' × ' + fmtLen(d.hole_d, u) + ' dia.']);
      }
    } else {
      out.push(['Size', fmtLen(d.w, u) + ' × ' + fmtLen(d.l, u)]);
      if (d.r > 0) out.push(['Corner radius', fmtLen(d.r, u)]);
      if (res.shape === 'frame') {
        out.push(['Opening', fmtLen(d.iw, u) + ' × ' + fmtLen(d.il, u) + ' (centered)']);
        if (d.ir > 0) out.push(['Opening radius', fmtLen(d.ir, u)]);
      }
      out.push(['Bolt holes', d.holes > 0 ? d.holes + ' × ' + fmtLen(d.hole_d, u) + ' dia., centers ' + fmtLen(d.e, u) + ' from edge' : 'None']);
    }
    if (res.psa) out.push(['Adhesive', (spec && spec.psaLabel) || 'PSA backing']);
    if (u === 'mm') out.push(['Units', 'Entered in millimeters']);
    return out;
  }

  function money(x, config) {
    var cur = (config && config.currency) || 'USD';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(x); }
    catch (e) { return '$' + (Math.round(x * 100) / 100).toFixed(2); }
  }

  /** Stable short fingerprint of a spec (for quote refs / de-duplication). */
  function fingerprint(spec) {
    var s = JSON.stringify([spec.shape, spec.units, spec.dims, spec.material, spec.thickness, spec.qty, !!spec.psa]);
    var h = 2166136261;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0).toString(36).toUpperCase();
  }

  return {
    SHAPES: SHAPES, FIELD_LABELS: FIELD_LABELS, MM_PER_IN: MM_PER_IN,
    num: num, normalizeDims: normalizeDims, geometry: geometry, validate: validate, rectHoles: rectHoles,
    quote: quote, money: money, fmtLen: fmtLen, fingerprint: fingerprint,
    findMaterial: findMaterial, findThickness: findThickness, tierFor: tierFor
  };
})();

window.RDGasketCore = RDGasketCore;
